// 问卷预览组件
// 基于 docs/问卷/交易所初筛问卷设计.md

// @ts-nocheck
import React from 'react';
import {
  Card,
  Descriptions,
  Typography,
  Row,
  Col,
  Tag,
  Space,
  Divider,
  Alert,
} from 'antd';
import {
  RocketOutlined,
  BankOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  TrophyOutlined,
  SafetyCertificateOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { InitialScreeningQuestionnaire } from '../../types/questionnaire';
import {
  ENTERPRISE_TYPE_LABELS,
  REGISTRATION_TYPE_LABELS,
  CREDIT_RATING_LABELS,
  INTERNATIONAL_RATING_LABELS,
  ASSET_SCALE_LABELS,
  INDUSTRY_LABELS,
  DEBT_RATIO_LABELS,
  PROFIT_SCALE_LABELS,
  CASH_FLOW_STATUS_LABELS,
  DEFAULT_RECORD_LABELS,
  GREEN_PROJECT_STATUS_LABELS,
  GREEN_PROJECT_CATEGORY_LABELS,
  GREEN_STANDARD_COMPLIANCE_LABELS,
  INVESTMENT_SCALE_LABELS,
  ISSUANCE_SCALE_LABELS,
  ISSUANCE_TIME_LABELS,
  PRODUCT_TYPE_LABELS,
  TARGET_MARKET_LABELS,
  LITIGATION_STATUS_LABELS,
  ENVIRONMENTAL_COMPLIANCE_LABELS,
  REGULATORY_APPROVAL_LABELS,
  URGENCY_LEVEL_LABELS,
} from '../../constants/questionnaire-options';
import { CONTACT_CONFIG } from '../../config/contact';

const { Title, Text } = Typography;

export interface QuestionnaireReviewProps {
  data: Partial<InitialScreeningQuestionnaire>;
}

export const QuestionnaireReview: React.FC<QuestionnaireReviewProps> = ({
  data,
}) => {
  const { t } = useTranslation();

  return (
    <div className="questionnaire-review-container" style={{ padding: '24px' }}>
      <div style={{ marginBottom: '32px' }}>
        <Title level={4} style={{ color: '#1f2937', marginBottom: '8px' }}>
          {t('questionnaire:review.title')}
        </Title>
        <Text type="secondary" style={{ fontSize: '14px' }}>
          {t('questionnaire:review.subtitle')}
        </Text>
      </div>

      {/* 项目基本信息 - 新增项目名称显示 */}
      {data.projectName && (
        <Card
          title={
            <div
              style={{
                background:
                  'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                margin: '-12px -16px 12px -16px',
                padding: '12px 16px',
                borderRadius: '12px 12px 0 0',
                borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
              }}
            >
              <Space>
                <RocketOutlined style={{ color: '#10b981' }} />
                <Text strong style={{ fontSize: '16px', color: '#374151' }}>
                  {t('questionnaire:review.projectInfo')}
                </Text>
              </Space>
            </div>
          }
          style={{
            marginBottom: '24px',
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(16, 185, 129, 0.1)',
            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          }}
          styles={{ body: { padding: '16px' } }}
        >
          <Descriptions column={1} size="small">
            <Descriptions.Item label={t('questionnaire:review.projectName')}>
              <Text strong style={{ color: '#10b981', fontSize: '16px' }}>
                {data.projectName}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* A. 发行主体资质核查 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <BankOutlined style={{ color: '#10b981' }} />
              <Text strong style={{ fontSize: '16px', color: '#374151' }}>
                {t('questionnaire:review.sectionA')}
              </Text>
            </Space>
          </div>
        }
        style={{
          marginBottom: '24px',
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
        }}
        styles={{ body: { padding: '16px' } }}
      >
        <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small">
          <Descriptions.Item label={t('questionnaire:review.field.issuerName')}>
            {data.issuerBasicInfo?.issuerName ||
              t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item label={t('questionnaire:review.field.guarantor')}>
            {data.issuerBasicInfo?.hasGuarantor
              ? data.issuerBasicInfo.guarantorName ||
                t('questionnaire:review.hasGuarantor')
              : t('questionnaire:review.noGuarantor')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.enterpriseType')}
          >
            {data.issuerBasicInfo?.enterpriseType
              ? t(
                  `questionnaire:options.enterpriseType.${data.issuerBasicInfo.enterpriseType}`,
                  ENTERPRISE_TYPE_LABELS[
                    data.issuerBasicInfo
                      .enterpriseType as keyof typeof ENTERPRISE_TYPE_LABELS
                  ] || data.issuerBasicInfo.enterpriseType
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.registrationLocation')}
          >
            {data.issuerBasicInfo?.registrationLocation
              ? t(
                  `questionnaire:options.registrationType.${data.issuerBasicInfo.registrationLocation}`,
                  REGISTRATION_TYPE_LABELS[
                    data.issuerBasicInfo
                      .registrationLocation as keyof typeof REGISTRATION_TYPE_LABELS
                  ] || data.issuerBasicInfo.registrationLocation
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.registrationNumber')}
          >
            {data.issuerBasicInfo?.registrationNumber || '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.creditRating')}
          >
            {data.issuerRatingScale?.creditRating
              ? t(
                  `questionnaire:options.creditRating.${data.issuerRatingScale.creditRating}`,
                  CREDIT_RATING_LABELS[
                    data.issuerRatingScale
                      .creditRating as keyof typeof CREDIT_RATING_LABELS
                  ]
                )
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.internationalRating')}
          >
            {data.issuerRatingScale?.internationalRating
              ? t(
                  `questionnaire:options.internationalRating.${data.issuerRatingScale.internationalRating}`,
                  INTERNATIONAL_RATING_LABELS[
                    data.issuerRatingScale
                      .internationalRating as keyof typeof INTERNATIONAL_RATING_LABELS
                  ]
                )
              : t('questionnaire:review.none')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.totalAssets')}
          >
            {data.issuerRatingScale?.totalAssets
              ? t(
                  `questionnaire:options.assetScale.${data.issuerRatingScale.totalAssets}`,
                  ASSET_SCALE_LABELS[
                    data.issuerRatingScale
                      .totalAssets as keyof typeof ASSET_SCALE_LABELS
                  ]
                )
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.industry')}
            span={2}
          >
            {data.issuerRatingScale?.industry
              ? t(
                  `questionnaire:options.industry.${data.issuerRatingScale.industry}`,
                  INDUSTRY_LABELS[
                    data.issuerRatingScale
                      .industry as keyof typeof INDUSTRY_LABELS
                  ] || data.issuerRatingScale.industry
                )
              : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* B. 财务状况评估 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <DollarOutlined style={{ color: '#10b981' }} />
              <Text strong style={{ fontSize: '16px', color: '#374151' }}>
                {t('questionnaire:review.sectionB')}
              </Text>
            </Space>
          </div>
        }
        style={{
          marginBottom: '24px',
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
        }}
        styles={{ body: { padding: '16px' } }}
      >
        <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small">
          <Descriptions.Item label={t('questionnaire:review.field.debtRatio')}>
            {data.financialStatus?.debtToAssetRatio
              ? t(
                  `questionnaire:options.debtRatio.${data.financialStatus.debtToAssetRatio}`,
                  DEBT_RATIO_LABELS[
                    data.financialStatus
                      .debtToAssetRatio as keyof typeof DEBT_RATIO_LABELS
                  ] || data.financialStatus.debtToAssetRatio
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item label={t('questionnaire:review.field.netProfit')}>
            {data.financialStatus?.netProfit
              ? t(
                  `questionnaire:options.profitScale.${data.financialStatus.netProfit}`,
                  PROFIT_SCALE_LABELS[
                    data.financialStatus
                      .netProfit as keyof typeof PROFIT_SCALE_LABELS
                  ] || data.financialStatus.netProfit
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.cashFlowStatus')}
          >
            {data.financialStatus?.cashFlowStatus
              ? t(
                  `questionnaire:options.cashFlowStatus.${data.financialStatus.cashFlowStatus}`,
                  CASH_FLOW_STATUS_LABELS[
                    data.financialStatus
                      .cashFlowStatus as keyof typeof CASH_FLOW_STATUS_LABELS
                  ] || data.financialStatus.cashFlowStatus
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.defaultRecord')}
          >
            {data.financialStatus?.defaultRecord
              ? t(
                  `questionnaire:options.defaultRecord.${data.financialStatus.defaultRecord}`,
                  DEFAULT_RECORD_LABELS[
                    data.financialStatus
                      .defaultRecord as keyof typeof DEFAULT_RECORD_LABELS
                  ] || data.financialStatus.defaultRecord
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* C. 绿色项目与发行计划 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <EnvironmentOutlined style={{ color: '#10b981' }} />
              <Text strong style={{ fontSize: '16px', color: '#374151' }}>
                {t('questionnaire:review.sectionC')}
              </Text>
            </Space>
          </div>
        }
        style={{
          marginBottom: '24px',
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
        }}
        styles={{ body: { padding: '16px' } }}
      >
        <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small">
          <Descriptions.Item
            label={t('questionnaire:review.field.greenProjectStatus')}
          >
            {data.greenProjectInfo?.hasGreenProject
              ? t(
                  `questionnaire:options.greenProjectStatus.${data.greenProjectInfo.hasGreenProject}`,
                  GREEN_PROJECT_STATUS_LABELS[
                    data.greenProjectInfo
                      .hasGreenProject as keyof typeof GREEN_PROJECT_STATUS_LABELS
                  ] || data.greenProjectInfo.hasGreenProject
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.greenStandardCompliance')}
          >
            {data.greenProjectInfo?.greenStandardCompliance
              ? t(
                  `questionnaire:options.greenStandardCompliance.${data.greenProjectInfo.greenStandardCompliance}`,
                  GREEN_STANDARD_COMPLIANCE_LABELS[
                    data.greenProjectInfo
                      .greenStandardCompliance as keyof typeof GREEN_STANDARD_COMPLIANCE_LABELS
                  ] || data.greenProjectInfo.greenStandardCompliance
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.projectInvestmentScale')}
          >
            {data.greenProjectInfo?.projectInvestmentScale
              ? t(
                  `questionnaire:options.investmentScale.${data.greenProjectInfo.projectInvestmentScale}`,
                  INVESTMENT_SCALE_LABELS[
                    data.greenProjectInfo.projectInvestmentScale
                  ] || data.greenProjectInfo.projectInvestmentScale
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.plannedIssuanceScale')}
          >
            {data.issuancePlan?.plannedIssuanceScale
              ? t(
                  `questionnaire:options.issuanceScale.${data.issuancePlan.plannedIssuanceScale}`,
                  ISSUANCE_SCALE_LABELS[
                    data.issuancePlan.plannedIssuanceScale
                  ] || data.issuancePlan.plannedIssuanceScale
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.plannedIssuanceTime')}
          >
            {data.issuancePlan?.plannedIssuanceTime
              ? t(
                  `questionnaire:options.issuanceTime.${data.issuancePlan.plannedIssuanceTime}`,
                  ISSUANCE_TIME_LABELS[data.issuancePlan.plannedIssuanceTime] ||
                    data.issuancePlan.plannedIssuanceTime
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.productType')}
          >
            {data.issuancePlan?.productType
              ? t(
                  `questionnaire:options.productType.${data.issuancePlan.productType}`,
                  PRODUCT_TYPE_LABELS[data.issuancePlan.productType] ||
                    data.issuancePlan.productType
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.targetMarket')}
            span={2}
          >
            {data.issuancePlan?.targetMarket
              ? t(
                  `questionnaire:options.targetMarket.${data.issuancePlan.targetMarket}`,
                  TARGET_MARKET_LABELS[data.issuancePlan.targetMarket] ||
                    data.issuancePlan.targetMarket
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.projectCategories')}
            span={2}
          >
            <Space wrap>
              {data.greenProjectInfo?.projectCategories &&
              data.greenProjectInfo.projectCategories.length > 0
                ? data.greenProjectInfo.projectCategories.map(category => (
                    <Tag key={category} color="green">
                      {t(
                        `questionnaire:options.greenProjectCategory.${category}`,
                        GREEN_PROJECT_CATEGORY_LABELS[
                          category as keyof typeof GREEN_PROJECT_CATEGORY_LABELS
                        ] || category
                      )}
                    </Tag>
                  ))
                : t('questionnaire:review.notFilled')}
            </Space>
          </Descriptions.Item>
          {data.projectDescription && (
            <Descriptions.Item
              label={t('questionnaire:review.field.projectDescription')}
              span={2}
            >
              <div
                style={{
                  padding: '12px',
                  background: 'rgba(16, 185, 129, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(16, 185, 129, 0.1)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {data.projectDescription}
              </div>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* D. 风险因素与联系信息 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <SafetyCertificateOutlined style={{ color: '#10b981' }} />
              <Text strong style={{ fontSize: '16px', color: '#374151' }}>
                {t('questionnaire:review.sectionD')}
              </Text>
            </Space>
          </div>
        }
        style={{
          marginBottom: '24px',
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
        }}
        styles={{ body: { padding: '16px' } }}
      >
        <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small">
          <Descriptions.Item
            label={t('questionnaire:review.field.majorLitigation')}
          >
            {data.riskFactors?.majorLitigation
              ? t(
                  `questionnaire:options.litigationStatus.${data.riskFactors.majorLitigation}`,
                  LITIGATION_STATUS_LABELS[data.riskFactors.majorLitigation] ||
                    data.riskFactors.majorLitigation
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.environmentalCompliance')}
          >
            {data.riskFactors?.environmentalCompliance
              ? t(
                  `questionnaire:options.environmentalCompliance.${data.riskFactors.environmentalCompliance}`,
                  ENVIRONMENTAL_COMPLIANCE_LABELS[
                    data.riskFactors.environmentalCompliance
                  ] || data.riskFactors.environmentalCompliance
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.regulatoryApproval')}
          >
            {data.riskFactors?.regulatoryApproval
              ? t(
                  `questionnaire:options.regulatoryApproval.${data.riskFactors.regulatoryApproval}`,
                  REGULATORY_APPROVAL_LABELS[
                    data.riskFactors.regulatoryApproval
                  ] || data.riskFactors.regulatoryApproval
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.urgencyLevel')}
          >
            {data.contactInfo?.urgencyLevel
              ? t(
                  `questionnaire:options.urgencyLevel.${data.contactInfo.urgencyLevel}`,
                  URGENCY_LEVEL_LABELS[data.contactInfo.urgencyLevel] ||
                    data.contactInfo.urgencyLevel
                )
              : t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('questionnaire:review.field.contactName')}
          >
            {data.contactInfo?.contactName ||
              t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item label={t('questionnaire:review.field.position')}>
            {data.contactInfo?.position || t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item label={t('questionnaire:review.field.phone')}>
            {data.contactInfo?.phone || t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          <Descriptions.Item label={t('questionnaire:review.field.email')}>
            {data.contactInfo?.email || t('questionnaire:review.notFilled')}
          </Descriptions.Item>
          {data.additionalInfo && (
            <Descriptions.Item
              label={t('questionnaire:review.field.additionalInfo')}
              span={2}
            >
              <div
                style={{
                  padding: '12px',
                  background: 'rgba(16, 185, 129, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(16, 185, 129, 0.1)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {data.additionalInfo}
              </div>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* 提交提示 */}
      <Alert
        message={t('questionnaire:review.submitNotice')}
        description={
          <div style={{ marginTop: '8px' }}>
            <Text style={{ fontSize: '13px', lineHeight: '1.6' }}>
              • {t('questionnaire:review.submitNoticeList.1')}
              <br />• {t('questionnaire:review.submitNoticeList.2')}
              <br />• {t('questionnaire:review.submitNoticeList.3')}
              <br />• {t('questionnaire:review.submitNoticeList.4')}
              <br />• {t('questionnaire:review.submitNoticeList.5')}
              {CONTACT_CONFIG.serviceHotline}
            </Text>
          </div>
        }
        type="warning"
        showIcon
        style={{ marginBottom: '24px' }}
      />
    </div>
  );
};

export default QuestionnaireReview;
